import { lazy, Suspense } from "react";
import { Navigate, Outlet, type RouteObject, useLocation } from "react-router";
import { useSessionReady, useUserToken } from "@/store/userStore";

const H5MallPage = lazy(() => import("@/pages/h5/mall"));
const H5TradePage = lazy(() => import("@/pages/h5/trade"));
const H5LotteryPage = lazy(() => import("@/pages/h5/lottery"));
const H5BrandPage = lazy(() => import("@/pages/h5/brand"));
const H5MinePage = lazy(() => import("@/pages/h5/mine"));
const H5LoginPage = lazy(() => import("@/pages/h5/login"));
const H5VersionPage = lazy(() => import("@/pages/h5/version"));
const H5RegisterPage = lazy(() => import("@/pages/h5/login").then((module) => ({ default: module.H5RegisterPage })));
const H5ForgotPasswordPage = lazy(() =>
	import("@/pages/h5/login").then((module) => ({ default: module.H5ForgotPasswordPage })),
);
const MineProfilePage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineProfilePage })));
const MineMessagesPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineMessagesPage })));
const MineMessageChatPage = lazy(() =>
	import("@/pages/h5/mine/messages").then((module) => ({ default: module.MineMessageChatPage })),
);
const MineOrdersPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineOrdersPage })));
const MineMarketPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineMarketPage })));
const MineRecommendPage = lazy(() =>
	import("@/pages/h5/mine").then((module) => ({ default: module.MineRecommendPage })),
);
const MineSharePage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineSharePage })));
const MineSettingsPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineSettingsPage })));
const MinePasswordPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MinePasswordPage })));
const MinePayPasswordPage = lazy(() =>
	import("@/pages/h5/mine").then((module) => ({ default: module.MinePayPasswordPage })),
);
const MineFeedbackPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineFeedbackPage })));
const MineFeedbackHistoryPage = lazy(() =>
	import("@/pages/h5/mine").then((module) => ({ default: module.MineFeedbackHistoryPage })),
);
const MineTermsPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineTermsPage })));
const MineAboutPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineAboutPage })));
const MineAddressPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineAddressPage })));
const MineBankCardsPage = lazy(() =>
	import("@/pages/h5/mine").then((module) => ({ default: module.MineBankCardsPage })),
);
const MineBankCardNewPage = lazy(() =>
	import("@/pages/h5/mine").then((module) => ({ default: module.MineBankCardNewPage })),
);
const MineAdvisorPage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineAdvisorPage })));
const MineServicePage = lazy(() => import("@/pages/h5/mine").then((module) => ({ default: module.MineServicePage })));
const ProductListPage = lazy(() =>
	import("@/pages/h5/mall/pages").then((module) => ({ default: module.ProductListPage })),
);
const ProductDetailPage = lazy(() =>
	import("@/pages/h5/mall/pages").then((module) => ({ default: module.ProductDetailPage })),
);
const ProductSharePage = lazy(() =>
	import("@/pages/h5/mall/pages").then((module) => ({ default: module.ProductSharePage })),
);
const AddCartPage = lazy(() => import("@/pages/h5/mall/pages").then((module) => ({ default: module.AddCartPage })));
const CartPage = lazy(() => import("@/pages/h5/mall/pages").then((module) => ({ default: module.CartPage })));
const CheckoutPage = lazy(() => import("@/pages/h5/mall/pages").then((module) => ({ default: module.CheckoutPage })));
const AddressNewPage = lazy(() =>
	import("@/pages/h5/mall/pages").then((module) => ({ default: module.AddressNewPage })),
);
const TradeWarehousePage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradeWarehousePage })),
);
const TradeTicketsPage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradeTicketsPage })),
);
const TradePointsPage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradePointsPage })),
);
const TradeOriginalPage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradeOriginalPage })),
);
const TradeBonusPage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradeBonusPage })),
);
const TradeLotteryRecordsPage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradeLotteryRecordsPage })),
);
const TradeContributionsPage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradeContributionsPage })),
);
const TradeRankingPage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradeRankingPage })),
);
const TradePromotionPage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradePromotionPage })),
);
const TradePromotionGivePage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradePromotionGivePage })),
);
const TradePromotionTransferPage = lazy(() =>
	import("@/pages/h5/trade/pages").then((module) => ({ default: module.TradePromotionTransferPage })),
);

const publicH5Paths = new Set([
	"/",
	"/home",
	"/trade",
	"/lottery",
	"/brand",
	"/mall/products",
	"/mall/products/detail",
	"/mall/products/detail/share",
	"/login",
	"/register",
	"/forgot-password",
	"/version",
]);
function H5AuthGuard() {
	const location = useLocation();
	const sessionReady = useSessionReady();
	const { accessToken } = useUserToken();
	if (!sessionReady) return null;
	const isLoggedIn = Boolean(accessToken?.trim());
	const isPublicProductDetail = location.pathname.startsWith("/mall/products/detail/");
	const isPublicPath = publicH5Paths.has(location.pathname) || isPublicProductDetail;

	if (!isLoggedIn && !isPublicPath) {
		return <Navigate to="/login" replace state={{ from: location.pathname }} />;
	}

	return <Outlet />;
}

export const h5Routes: RouteObject[] = [
	{
		element: (
			<Suspense>
				<H5AuthGuard />
			</Suspense>
		),
		children: [
			{ index: true, element: <H5MallPage /> },
			{ path: "home", element: <H5MallPage /> },
			{ path: "trade", element: <H5TradePage /> },
			{ path: "lottery", element: <H5LotteryPage /> },
			{ path: "brand", element: <H5BrandPage /> },
			{ path: "mine", element: <H5MinePage /> },
			{ path: "mine/profile", element: <MineProfilePage /> },
			{ path: "mine/messages", element: <MineMessagesPage /> },
			{ path: "mine/messages/:userId", element: <MineMessageChatPage /> },
			{ path: "mine/orders", element: <MineOrdersPage /> },
			{ path: "mine/market", element: <MineMarketPage /> },
			{ path: "mine/recommend", element: <MineRecommendPage /> },
			{ path: "mine/share", element: <MineSharePage /> },
			{ path: "mine/settings", element: <MineSettingsPage /> },
			{ path: "mine/password", element: <MinePasswordPage /> },
			{ path: "mine/pay-password", element: <MinePayPasswordPage /> },
			{ path: "mine/feedback", element: <MineFeedbackPage /> },
			{ path: "mine/feedback/history", element: <MineFeedbackHistoryPage /> },
			{ path: "mine/terms", element: <MineTermsPage /> },
			{ path: "mine/about", element: <MineAboutPage /> },
			{ path: "mine/address", element: <MineAddressPage /> },
			{ path: "mine/bank-cards", element: <MineBankCardsPage /> },
			{ path: "mine/bank-cards/new", element: <MineBankCardNewPage /> },
			{ path: "mine/advisor", element: <MineAdvisorPage /> },
			{ path: "mine/service", element: <MineServicePage /> },
			{ path: "trade/warehouse", element: <TradeWarehousePage /> },
			{ path: "trade/point", element: <TradePointsPage /> },
			{ path: "trade/points", element: <TradePointsPage /> },
			{ path: "trade/original", element: <TradeOriginalPage /> },
			{ path: "trade/tickets", element: <TradeTicketsPage /> },
			{ path: "trade/bonus", element: <TradeBonusPage /> },
			{ path: "trade/lottery-records", element: <TradeLotteryRecordsPage /> },
			{ path: "trade/contributions", element: <TradeContributionsPage /> },
			{ path: "trade/ranking", element: <TradeRankingPage /> },
			{ path: "trade/promotion", element: <TradePromotionPage /> },
			{ path: "trade/promotion/give", element: <TradePromotionGivePage /> },
			{ path: "trade/promotion/transfer", element: <TradePromotionTransferPage /> },
			{ path: "mall/products", element: <ProductListPage /> },
			{ path: "mall/products/detail", element: <ProductDetailPage /> },
			{ path: "mall/products/detail/:productId", element: <ProductDetailPage /> },
			{ path: "mall/products/detail/share", element: <ProductSharePage /> },
			{ path: "mall/products/detail/add-cart", element: <AddCartPage /> },
			{ path: "mall/cart", element: <CartPage /> },
			{ path: "mall/checkout", element: <CheckoutPage /> },
			{ path: "mall/address/new", element: <AddressNewPage /> },
			{ path: "login", element: <H5LoginPage /> },
			{ path: "register", element: <H5RegisterPage /> },
			{ path: "forgot-password", element: <H5ForgotPasswordPage /> },
			{ path: "version", element: <H5VersionPage /> },
		],
	},
];
